import { Dataset } from '@atoms/Dataset/Dataset';
import { NewDatasetAtom } from '@atoms/Dataset/NewDataset';
import { getOnFormatChangeHandler } from '@common/gem/FormatList';
import { GraphStates, graphStatesSelector, useCurrentProcess } from '@common/selectors';
import { CommonActionTypes, CommonGraph, CommonState, ComponentInfo } from '@common/types';
import { useLSPMethod } from '@LSP/base/hook';
import { LSP } from '@LSP/base/types';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { isObjectEqual, removeEmptyParams } from '@prophecy/utils/data';
import { useAfterNextRender, useOnMount, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEventState } from '@prophecy/utils/react/useEventState';
import { COMPONENT_PREFIX } from 'frontend/core/src/Parser/bindings';
import { WorkflowGraph } from 'frontend/core/src/redux/types';
import { nanoid } from 'nanoid';
import { ComponentProps, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from 'styled-components';

import { WorkflowDialog } from '../../Workflow/WorkflowDialog';
import { MockWorkflowContext } from './mockWorkFlowContext';

const StyledSpinner = styled(Spinner)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TranspilerDataset = (props: ComponentProps<typeof Dataset>) => {
  const { elementId, actions, bindings } = props;
  const { currentDatasetRef } = useContext(MockWorkflowContext);
  const [loading, setLoading] = useState(true);

  useOnMount(function () {
    if (currentDatasetRef?.current?.name) {
      createNewDataset(currentDatasetRef?.current?.name);
    }
  });

  const { method: didAction } = useLSPMethod(LSP.Method.propertiesDidAction);
  const { method: didChange } = useLSPMethod(LSP.Method.propertiesDidChange);

  async function createNewDataset(name: string) {
    if (actions?.includes('openCreateDatasetDialog')) {
      const mergeId = nanoid();
      await didAction(
        {
          elementId: elementId,
          actionName: 'openCreateDatasetDialog'
        },
        {
          historyMergeId: mergeId
        }
      );
      await didChange(
        [
          {
            property: bindings.newDatasetName,
            value: name
          },
          { property: bindings.datasetId, value: '' }
        ],
        {
          historyMergeId: mergeId
        }
      );
    }
    setLoading(false);
  }

  return (
    <StyledSpinner style={{ height: 500 }} spinning={loading}>
      {loading ? null : <Dataset {...props} />}
    </StyledSpinner>
  );
};

export const TranspilerNewDataset = (props: ComponentProps<typeof NewDatasetAtom>) => {
  const { currentDatasetRef, callbackRef, wipStateRef } = useContext(MockWorkflowContext);
  const dispatch = useDispatch();
  const { bindings } = props;
  // oxlint-disable-next-line typescript/no-non-null-asserted-optional-chain
  const initialDataset = currentDatasetRef?.current!;
  const process = useCurrentProcess<WorkflowGraph>();
  const { method: didChange } = useLSPMethod(LSP.Method.propertiesDidChange);
  const onFormatChange = usePersistentCallback(getOnFormatChangeHandler(bindings, process!, didChange));

  const setWipRef = usePersistentCallback(() => {
    wipStateRef.current = process!;
  });

  const { afterNextRender } = useAfterNextRender();

  useOnMount(function () {
    if (!initialDataset) {
      dispatch({ type: CommonActionTypes.dialogClose });
      return;
    }

    const processProperties = process?.properties as processProperty;
    const newDataset = initialDataset.newDataset?.newDataset;

    if (!newDataset) {
      dispatch({ type: CommonActionTypes.dialogClose });
      return;
    }

    const { datasetType, format: datasetFormat, ...restDatasetProperties } = newDataset;

    let dataFormats = processProperties.dataFormats.filter(
      (format) => format.value === datasetFormat && format.type === datasetType
    );

    let dataFormat: { value: string; type: string; componentInfo: { projectName: string } };
    type processProperty = {
      dataFormats: Array<typeof dataFormat>;
      newDataset: { componentInfo: ComponentInfo };
    };

    if (dataFormats.length > 1) {
      dataFormat = dataFormats.find(
        (format) => format.componentInfo.projectName === processProperties.newDataset.componentInfo.projectName
      )!;
    } else {
      dataFormat = dataFormats[0];
    }

    if (!dataFormat) {
      dispatch({ type: CommonActionTypes.dialogClose });
      return;
    }
    onFormatChange(dataFormat.value, dataFormat.type, dataFormat.componentInfo as ComponentInfo).then(() => {
      const parts = bindings.datasetType.replace(COMPONENT_PREFIX, '').split('.');
      const parentPath = parts.slice(0, -1).join('.');
      delete restDatasetProperties.componentInfo;
      Object.entries(removeEmptyParams(restDatasetProperties)).forEach(([key, value]) => {
        didChange({ property: `${COMPONENT_PREFIX}${parentPath}.${key}`, value });
      });
      afterNextRender(setWipRef);
    });
  });

  return <NewDatasetAtom {...props} customOnSave={callbackRef.current?.updateCurrentDataset} hideBreadCrumbs={true} />;
};

export function MockWorkflowDialog({ children, actions = [] }: { children: React.ReactNode; actions: string[] }) {
  const { wipStateRef } = useContext(MockWorkflowContext);
  const getGraphStates = useEventState<CommonState, GraphStates<CommonGraph>>(
    graphStatesSelector<CommonGraph, CommonState>
  );

  const hasUnsavedChangesCheck = usePersistentCallback(() => {
    const { wipState } = getGraphStates();
    let pid = wipStateRef.current!.id;
    return !isObjectEqual(wipState?.processes[pid].properties, wipStateRef.current!.properties, []);
  });

  return (
    <WorkflowDialog actions={actions} hasUnsavedChangesCheck={hasUnsavedChangesCheck}>
      {children}
    </WorkflowDialog>
  );
}
